<template>
  <DynamicScroller
    v-if="items.length"
    ref="scroller"
    tabindex="-1"
    class="scroller"
    :items="items"
    :buffer="bufferSizeInPixels"
    :min-item-size="approximateItemSize"
    :emit-update="true"
    @update="update"
    @visible="visible = true"
    @hidden="visible = false"
  >
    <template #before>
      <slot name="before" />
    </template>
    <template #default="{ item, index, active }">
      <DynamicScrollerItem :item="item" :active="active" :data-index="index">
        <slot name="item" :item="item" />
      </DynamicScrollerItem>
    </template>
    <template #after>
      <slot name="after" />
    </template>
  </DynamicScroller>
</template>

<script setup lang="ts">
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import { until } from '@vueuse/core'
import { ref } from 'vue'

const visible = ref<boolean>(false)
const scroller = ref<any | null>() // library does not use typescript :(

const props = withDefaults(
  defineProps<{
    items: Readonly<any[]> | any[]
    loadMoreThreshold?: number
    approximateItemSize?: number
    bufferSizeInPixels?: number
  }>(),
  {
    loadMoreThreshold: 10,
    approximateItemSize: 60,
    bufferSizeInPixels: 100,
  },
)
const emit = defineEmits<{
  (event: 'near-bottom'): void
}>()

const update = (
  _startIndex: number,
  _endIndex: number,
  _visibleStartIndex: number,
  _visibleEndIndex: number,
) => {
  if (_visibleEndIndex + props.loadMoreThreshold > props.items.length) {
    emit('near-bottom')
  }
}

const scrollToPosition = async (position: number) => {
  await until(visible).toBe(true)

  if (scroller?.value?.$refs?.scroller) {
    scroller?.value.$refs.scroller.scrollToPosition(position)
  }
}

const getScrollPosition = () => {
  return scroller?.value.$refs.scroller.getScroll().start
}

defineExpose({
  scrollToPosition,
  getScrollPosition,
  visible,
})
</script>

<style scoped>
.scroller {
  overflow-y: scroll;
  height: 100%;
  flex-grow: 1;
}
</style>
